<template>
  <div class="inline-flex flex-col">
    <slot
      v-if="!withoutLabel"
      name="label"
    >
      <mia-feature-label
        :for="uniqueId"
        text="Modellreihe"
      />
    </slot>

    <div v-tooltip="tooltip">
      <mia-select
        v-if="requestKey === null"
        :value="value"
        class="w-full"
        :class="{ 'opacity-50 cursor-not-allowed': isDisabled }"
        :disabled="isDisabled"
        :data-highlight="dataHighlight"
        @input="$emit('input', $event)"
      >
        <option value="">
          Modellreihe auswählen
        </option>
        <option
          v-for="range in modelRanges"
          :key="range"
          :value="range"
          v-text="range"
        />
      </mia-select>
    </div>
  </div>
</template>

<script>
import { getModelRange } from '@/api/mobile'
import FeatureLabel from '@/components/cars/edit/FeatureLabel.vue'
import { isEmpty } from '@/helpers/utils'

export default {
  name: 'MiaModelRangeSelect',

  components: {
    'mia-feature-label': FeatureLabel
  },

  props: {
    value: {
      type: String,
      required: true
    },

    make: {
      required: true,
      validator(make) {
        return make === null || typeof make === 'string'
      }
    },

    model: {
      required: true,
      validator(model) {
        return model === null || typeof model === 'string'
      }
    },

    initialRegistration: {
      default: '',
      validator(initialRegistration) {
        return initialRegistration === null || typeof initialRegistration === 'string'
      }
    },

    withoutLabel: {
      type: Boolean,
      default: false
    },

    dataHighlight: {
      type: String,
      default: ''
    }
  },

  data() {
    return {
      modelRanges: [],
      requestKey: null
    }
  },

  computed: {
    attrs() {
      return {
        placeholder: 'Modell auswählen',
        ...this.$attrs,
        'custom-label': this.getLabel,
        flatten: true,
        id: this.uniqueId,
        options: [],
        'track-by': 'name',
        value: this.value
      }
    },

    listeners() {
      return {
        ...this.$listeners,
        input: event => this.$emit('input', event || '')
      }
    },

    uniqueId() {
      // eslint-disable-next-line no-underscore-dangle
      return `model-range-${this._uid}`
    },

    isDisabled() {
      return isEmpty(this.make) || isEmpty(this.model)
    },

    tooltip() {
      if (this.isDisabled) {
        return 'Bitte wählen Sie zunächst Marke und Modell'
      }

      return ''
    }
  },

  watch: {
    make: {
      immediate: true,
      async handler(make) {
        if (isEmpty(make)) {
          this.reset()
        } else if (!isEmpty(this.model)) {
          this.fetchModelRanges(make, this.model, this.initialRegistration)
        }
      }
    },

    model: {
      immediate: true,
      handler(model) {
        if (isEmpty(model)) {
          this.reset()
        } else if (!isEmpty(this.make)) {
          this.fetchModelRanges(this.make, model, this.initialRegistration)
        }
      }
    },

    initialRegistration: {
      immediate: true,
      handler(initialRegistration) {
        this.fetchModelRanges(this.make, this.model, initialRegistration)
      }
    }
  },

  methods: {
    getLabel(id) {
      return this.selectableModels.find(({ name }) => name === id).description
    },

    reset() {
      this.modelRanges = []
      this.$emit('input', '')
    },

    async fetchModelRanges(make, model, initialRegistration) {
      const requestKey = `${make}.${model}.${initialRegistration}`

      // A request for the same data is already in progress
      if (this.requestKey !== null && requestKey === this.requestKey) {
        return
      }

      this.requestKey = requestKey
      this.modelRanges = await getModelRange(make, model, initialRegistration || '')
      this.requestKey = null
    }
  }
}
</script>
